import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFECT_STATUS,
  DEFECT_STATUS_TRANSITIONS,
  type DefectListEntry,
  type DefectStatus,
  type SeverityLevel,
  canInAnyScope,
  canTransition,
} from '@testflow/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel, TestCaseTitleLine } from '../../components/atoms.js';
import { KanbanColumn, SeverityBadge } from '../../components/board.js';
import { CardMenu } from '../../components/card-menu.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { SidePanel } from '../../components/side-panel.js';
import { defectsApi } from '../../lib/defects-api.js';
import {
  isInvalidTransitionError,
  isReporterPendingError,
  isRetestMissingError,
  mapApiError,
} from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { toast } from '../../lib/toast.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useRowSelection } from '../../lib/use-row-selection.js';
import { useUrlState } from '../../lib/use-url-state.js';
import { DefectCard } from './defect-card.js';
import { DefectCreateDialog } from './defect-create-dialog.js';
import { DefectDetail } from './defect-detail.js';
import { RetestGuardDialog } from './retest-guard-dialog.js';

const COLUMN_STATUS: DefectStatus[] = [
  'neu',
  'in_analyse',
  'in_behebung',
  'retest_bereit',
  'geschlossen',
];
// abgelehnt teilt sich die Spalte mit geschlossen (Design-Spec §3.4), behält aber sein Label.
const columnFor = (s: DefectStatus): DefectStatus => (s === 'abgelehnt' ? 'geschlossen' : s);

// Review-Fix Task 14 (Important 2): Sonderwert fuer "Bearbeiter entfernen" als Select-Option statt
// eigenem Knopf — unterscheidet sich von '' (= keine Auswahl / Platzhalter), damit der
// Uebernehmen-Knopf beide Faelle auseinanderhalten kann.
const REMOVE_ASSIGNEE = '__remove__';

interface Lane {
  node_id: string | null;
  node_name: string | null;
  items: DefectListEntry[];
}

// Task 5 (Drag & Drop) verwendet dieselben Variablen und dieselbe Mutation — deshalb hier als
// eigener Typ statt inline.
interface TransitionVars {
  defectId: string;
  humanId: string;
  testCaseHumanId: string | null;
  to: DefectStatus;
  // #87: nur beim Retry aus dem Begruendungs-Dialog gesetzt — der Erstversuch schickt keinen
  // Kommentar, s. transition.mutationFn unten (haelt bestehende 3-Argumente-Aufrufe unveraendert).
  comment?: string;
}

interface AssignVars {
  defectId: string;
  humanId: string;
  assigneeId: string | null;
  assigneeName: string | null;
}

export function DefectBoardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id ?? '';
  // #36: Ansicht + Filter leben in der URL-Query (deep-linkbar, reload-fest, teilbar) — dasselbe
  // zentrale Muster wie auf den anderen Listenseiten. Der Freitext-Filter (`textFilter`, s. u.) ist
  // bewusst ausgenommen. `view` wird auf die beiden gültigen Werte normalisiert, damit ein
  // manipuliertes `?view=xyz` auf die Kanban-Vorgabe zurückfällt statt eine leere Fläche zu zeigen.
  const [viewParam, setView] = useUrlState('view', 'kanban');
  const view: 'kanban' | 'table' = viewParam === 'table' ? 'table' : 'kanban';
  const [severityFilter, setSeverityFilter] = useUrlState('severity', '');
  const [assigneeFilter, setAssigneeFilter] = useUrlState('assignee', '');
  // #57: '' = alle Plaene · UUID = genau dieser Plan · 'none' = nur Defects ohne Plan. Der Wert
  // wandert wie Severity und Bearbeiter in den Query-Key, wird also serverseitig gefiltert.
  const [planFilter, setPlanFilter] = useUrlState('plan', '');
  // #90: '' = alle · 'open' = nur untriagierte (severity_triaged === false). Clientseitig wie
  // textFilter (Kommentar dort) — kein Server-Roundtrip fuer einen Filter, den das Board mit den
  // ohnehin schon geladenen Defects beantworten kann.
  const [triageFilter, setTriageFilter] = useUrlState('triage', '');
  // #91: Checkbox-Spalte + Sammel-Aktionsleiste NUR bei `defect.edit` — dieselbe Rechte-Prüfung wie
  // Status-/Bearbeiterwechsel serverseitig verlangt. useScopedUser beschneidet auf GENAU dieses
  // Projekt (Tenant-Isolation, s. project-roles.ts).
  const { user } = useScopedUser(projectId);
  const canBulk = user ? canInAnyScope(user, 'defect.edit') : false;
  // #37 (Finding 32): bewusst clientseitig, waehrend Severity und Zustaendig serverseitig im
  // Query-Key bleiben. Ein Roundtrip pro Tastenanschlag waere der falsche Preis — und das Board
  // laedt in V1 ohnehin alle Defects des Projekts.
  const [textFilter, setTextFilter] = useState('');
  // #91: Auswahl = fluechtige Arbeitsablage, nie URL. resetKey traegt den Filter-Fingerabdruck aus
  // ALLEN Filtern der Seite + view — inklusive des clientseitigen textFilter: der Hook-Vertrag
  // (use-row-selection.ts) verlangt, dass Sammelaktionen nie unsichtbar markierte Zeilen treffen,
  // und der Freitext-Filter blendet Zeilen genauso aus wie die URL-Filter (Muster scope-table.tsx).
  const selection = useRowSelection(
    `${severityFilter}|${assigneeFilter}|${planFilter}|${view}|${textFilter}|${triageFilter}`,
  );
  // #91: lokale Eingabe fuer die Sammel-Severity — erst der Uebernehmen-Knopf loest bulkUpdate aus
  // (kein onChange-Sofortversand, damit eine Fehlbedienung des Selects nicht sofort schreibt).
  // Review-Fix Task 14 (Important 2): der Bearbeiter-Zweig war ehemals sofort-ausloesend (analog
  // TesterCell) — bei N markierten Defects ohne Bestaetigung zu riskant (Kontrolleur-Entscheid).
  // Jetzt symmetrisch zur Severity: eigener State, erst der (gemeinsame) Uebernehmen-Knopf schreibt.
  const [bulkSeverityId, setBulkSeverityId] = useState('');
  const [bulkAssigneeId, setBulkAssigneeId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  // #37 (Finding 29): Der Defect oeffnet sich neben dem Board statt an dessen Stelle. Titel und
  // human_id kommen aus dem Listeneintrag — damit steht die Panel-Ueberschrift sofort, ohne auf
  // defectsApi.get zu warten.
  const [openDefect, setOpenDefect] = useState<{
    id: string;
    human_id: string;
    title: string;
  } | null>(null);
  // Strukturell typisiert statt auf DefectListEntry/Defect festgelegt: beide Typen erfuellen die
  // drei Felder, dadurch kann sowohl ein Board-Klick als auch onCreated (Defect aus der API)
  // dieselbe Funktion nutzen, ohne dass hier zwei fast identische Objekt-Literale gepflegt werden.
  const openFrom = (d: { defect_id: string; human_id: string; title: string }) =>
    setOpenDefect({ id: d.defect_id, human_id: d.human_id, title: d.title });
  // useCallback mit stabiler Identitaet: SidePanel legt den Fokus in seinem useEffect([onClose])
  // erneut aufs Dialog-Element, sobald sich onClose aendert. Ein inline definiertes onClose hat
  // bei jedem Board-Rerender (z. B. jeder Tastenanschlag im Freitext-Filter) eine neue Identitaet
  // — der Fokus wuerde dann aus dem gerade getippten Feld zurueck ins Panel gerissen.
  const closePanel = useCallback(() => {
    // Titel/Beschreibung speichern per onBlur — beim Unmount feuert kein focusout mehr, deshalb
    // den Fokus vorher aktiv abgeben. Escape ist der Weg, der sonst Eingaben verliert: Backdrop-
    // Klick und das X blurren das Feld schon vorher per mousedown.
    (document.activeElement as HTMLElement | null)?.blur();
    setOpenDefect(null);
  }, []);
  // #37 (Finding 31): Zugeklappt bleibt die Kopfzeile stehen — Name, Anzahl und Blocker-Zaehler
  // sind die Uebersicht, die der Nutzer bei vielen Lanes sucht. Bewusst ohne Persistenz: die kommt
  // mit #36 fuer alle Seiten gemeinsam.
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(new Set());
  const toggleLane = (key: string) =>
    setCollapsedLanes((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // #43 Task 5: Ziehen und Ablegen als Abkuerzung auf dieselbe transition-Mutation — keine
  // eigene Regel, kein optimistisches Verschieben.
  const [dragging, setDragging] = useState<{
    defect_id: string;
    human_id: string;
    test_case_human_id: string | null;
    status: DefectStatus;
    laneKey: string;
  } | null>(null);
  // Rueckfrage beim geteilten Endzustand: dieselbe Auswahl wie auf der Karte, nur mit zwei Zielen.
  // laneKey/col verankern das Menue an der Spalte, in der abgelegt wurde (Review-Fix Minor 7,
  // Design-Spec §4.2: "an der Ablegestelle", nicht am Seitenende).
  const [dropChoice, setDropChoice] = useState<{
    vars: Omit<TransitionVars, 'to'>;
    targets: DefectStatus[];
    laneKey: string;
    col: DefectStatus;
  } | null>(null);
  // #87: haelt die Variablen des zuletzt an retest_missing gescheiterten Uebergangs fest, solange
  // der Begruendungs-Dialog offen ist — egal ob der Uebergang ueber Statusmenue, Drag&Drop oder die
  // Rueckfrage beim geteilten Endzustand ausgeloest wurde, alle drei teilen sich dieselbe Mutation.
  // #194: `reason` waehlt den Dialogtext (Nachtest oder Melder-Bestaetigung), `reporterName` kommt
  // aus dem Listeneintrag der Karte (awaiting_reporter_name).
  const [retestGuard, setRetestGuard] = useState<{
    vars: TransitionVars;
    reason: 'retest_missing' | 'reporter_pending';
    reporterName: string | null;
  } | null>(null);

  // Review-Fix Important 2: `useMutation` ist EINE Instanz je Mutation — `variables` zeigt immer
  // auf den zuletzt gestarteten `mutate`-Aufruf. Schaltet man Karte A um und sofort Karte B, sah A
  // sich augenblicklich wieder ungedaempft (aria-busy="false", Knopf aktiv, ziehbar), obwohl A's
  // Anfrage noch offen war. Statt an `variables` haengt busy jetzt an einer Menge tatsaechlich
  // laufender Defect-IDs, die beide Mutationen (Status UND Bearbeiter) gemeinsam pflegen — busy
  // deckt schliesslich beide ab (s. DefectCard-Prop).
  const [pendingDefectIds, setPendingDefectIds] = useState<Set<string>>(new Set());
  const markPending = (defectId: string) =>
    setPendingDefectIds((prev) => new Set(prev).add(defectId));
  const clearPending = (defectId: string) =>
    setPendingDefectIds((prev) => {
      const next = new Set(prev);
      next.delete(defectId);
      return next;
    });

  const filters = {
    ...(severityFilter ? { severity_id: severityFilter } : {}),
    ...(assigneeFilter ? { assignee_id: assigneeFilter } : {}),
    ...(planFilter ? { test_plan_id: planFilter } : {}),
  };

  // #108: volle Query-Objekte statt schlanker Destrukturierung — die drei Sekundaer-Queries
  // scheiterten bislang still (nur `data` genutzt), die Fehlerzeile im Kopfbereich unten braucht
  // isError + refetch je Query, damit Retry gezielt nur die Gescheiterten erneut anstoesst.
  const configQuery = useQuery({
    queryKey: ['defect-config', projectId],
    queryFn: () => defectsApi.config(projectId),
    enabled: Boolean(projectId),
  });
  const membersQuery = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => plansApi.members(projectId),
    enabled: Boolean(projectId),
  });
  // #57: derselbe Query-Key wie in Reporting/Sign-off/My-Tests — die Liste ist ohnehin im Cache.
  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const config = configQuery.data;
  const members = membersQuery.data;
  const plans = plansQuery.data;
  const defectsQuery = useQuery({
    queryKey: ['defects', projectId, filters],
    queryFn: () => defectsApi.list(projectId, filters),
    enabled: Boolean(projectId),
  });
  const { data: defects, isLoading, isError } = defectsQuery;

  const queryClient = useQueryClient();
  // Eine Mutation fuer das ganze Board, der Defect kommt als Variable. Eine useMutation je Karte
  // wuerde die Antwort an eine Kartenposition binden — und die Karte wandert genau jetzt (#55).
  // #87: `errorToast: false` — der Begruendungs-Dialog ist das Fehler-Feedback fuer retest_missing,
  // ein zusaetzlicher globaler Toast waere die doppelte Meldung. Fuer jeden ANDEREN Fehler (z. B.
  // invalid_transition bei einer Race zwischen zwei Nutzern) uebernimmt onError unten von Hand
  // genau das, was der globale Handler (query-client.ts) sonst getan haette.
  const transition = useMutation({
    mutationFn: (v: TransitionVars) =>
      v.comment
        ? defectsApi.transition(projectId, v.defectId, v.to, v.comment)
        : defectsApi.transition(projectId, v.defectId, v.to),
    meta: { errorToast: false },
    onMutate: (v) => markPending(v.defectId),
    onSettled: (_res, _err, v) => clearPending(v.defectId),
    onError: (err, v) => {
      if (isRetestMissingError(err)) {
        setRetestGuard({ vars: v, reason: 'retest_missing', reporterName: null });
        return;
      }
      // #194: gleicher Weg, anderer Text. Der Name steht auf dem Listeneintrag, nicht in den
      // Variablen — so muessen die drei Ausloeser (Menue, Drag&Drop, Rueckfrage) nichts mitgeben.
      if (isReporterPendingError(err)) {
        const entry = defects?.find((x) => x.defect_id === v.defectId);
        setRetestGuard({
          vars: v,
          reason: 'reporter_pending',
          reporterName: entry?.awaiting_reporter_name ?? null,
        });
        return;
      }
      toast.error(mapApiError(t, err));
      // Follow-up #194: Verlorenes Rennen (ein anderer hat den Status schon gewechselt) — die Karte
      // steht noch in ihrer alten Spalte. Neu laden, damit sie in ihre echte Spalte springt.
      if (isInvalidTransitionError(err)) {
        void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
        void queryClient.invalidateQueries({ queryKey: ['defect', projectId, v.defectId] });
      }
    },
    onSuccess: (res, v) => {
      setRetestGuard(null);
      // Kein Erfolgs-Toast: die Karte wandert sichtbar. Nur die Kaskade ist eine Nachricht, die
      // sonst niemand mitbekaeme — und sie braucht den Testfallnamen, deshalb imperativ statt
      // ueber meta.successToast (das nimmt nur einen statischen Schluessel).
      if (res.retest_cascaded && v.testCaseHumanId) {
        toast.success(t('defect.retestCascadedToast', { testCase: v.testCaseHumanId }));
      }
      void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['defect', projectId, v.defectId] });
    },
  });

  const assign = useMutation({
    mutationFn: (v: AssignVars) =>
      defectsApi.patch(projectId, v.defectId, { assignee_id: v.assigneeId }),
    onMutate: (v) => markPending(v.defectId),
    onSettled: (_res, _err, v) => clearPending(v.defectId),
    onSuccess: (_res, v) => {
      // Ist der Bearbeiter-Filter aktiv und die Karte faellt dadurch heraus, verschwindet sie
      // ohne Erklaerung. Genau dann — und nur dann — sagen wir es.
      if (assigneeFilter !== '' && v.assigneeId !== assigneeFilter) {
        toast.success(
          t('defect.assign.hiddenByFilter', {
            id: v.humanId,
            name: v.assigneeName ?? t('defect.unassigned'),
          }),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['defect', projectId, v.defectId] });
    },
  });

  // #91: Sammel-Severity/-Bearbeiter ueber die Tabellen-Aktionsleiste — EIN Request statt N
  // Einzel-PATCHes. Schlaegt der GESAMTE Request fehl (z. B. HTTP 422 bei fremder Severity/fremdem
  // Bearbeiter), reicht der globale Fehler-Toast (query-client.ts); die Auswahl bleibt fuer den
  // Retry stehen (kein selection.clear() im Fehlerfall).
  const bulkUpdate = useMutation({
    mutationFn: (body: {
      defect_ids: string[];
      patch: { severity_id?: string; assignee_id?: string | null };
    }) => defectsApi.bulkUpdate(projectId, body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['defects', projectId] });
      selection.clear();
      // Review-Fix Task 14 (Important 1): ohne diesen Reset blieb die zuletzt gewaehlte Severity
      // (bzw. seit Fix 2 auch der Bearbeiter) im Select stehen — eine NEUE Auswahl haette sie
      // versehentlich mit uebernommen, obwohl die Leiste nach Erfolg kurz verschwindet und beim
      // naechsten Markieren neu erscheint.
      setBulkSeverityId('');
      setBulkAssigneeId('');
      announceBulkResult(result);
    },
  });

  // Review-Fix Minor 3: die Rueckfrage beim geteilten Endzustand wird nur gerendert, wenn Lane
  // UND Spalte noch passen (s. JSX weiter unten) — klappt die Lane zu oder filtert sie weg,
  // verschwindet das Menue rein optisch, OHNE dass sein onClose laeuft. dropChoice bliebe gesetzt,
  // und beim Wiederaufklappen erschiene die alte Rueckfrage erneut und risse den Fokus an sich.
  // Dieser Effekt raeumt dropChoice explizit auf, sobald seine Lane zu- oder herausgefiltert wird.
  useEffect(() => {
    if (!dropChoice) return;
    if (collapsedLanes.has(dropChoice.laneKey)) {
      setDropChoice(null);
      return;
    }
    const q = textFilter.trim().toLowerCase();
    const laneStillVisible = (defects ?? []).some((d) => {
      const key = d.node_id ?? '__none__';
      if (key !== dropChoice.laneKey) return false;
      return (
        q === '' ||
        d.human_id.toLowerCase().includes(q) ||
        d.title.toLowerCase().includes(q) ||
        (d.tcode ?? '').toLowerCase().includes(q)
      );
    });
    if (!laneStillVisible) setDropChoice(null);
  }, [dropChoice, collapsedLanes, textFilter, defects]);

  // Die eine Regel, aus der Hervorhebung UND Drop-Entscheidung folgen. Zweite Regeltabelle gibt es
  // nicht — Ziele kommen ausschliesslich aus canTransition. Welche Stati ueberhaupt in `col`
  // landen, weiss bereits `columnFor` (Review-Fix Minor 5): frueher stand das Paar
  // ['geschlossen', 'abgelehnt'] hier ein zweites Mal woertlich, jetzt wird es aus columnFor
  // abgeleitet — ein dritter Status in einer geteilten Spalte braucht nur noch eine Aenderung.
  const targetsFor = (from: DefectStatus, col: DefectStatus): DefectStatus[] =>
    DEFECT_STATUS.filter((s) => columnFor(s) === col && canTransition(from, s));

  const dropStateFor = (col: DefectStatus, laneKey: string) => {
    if (!dragging) return 'idle' as const;
    if (dragging.laneKey !== laneKey) return 'denied' as const;
    if (columnFor(dragging.status) === col) return 'source' as const;
    return targetsFor(dragging.status, col).length > 0 ? ('allowed' as const) : ('denied' as const);
  };

  // Reihenfolge ist normativ (Spec §4.2): Lane -> Ausgangsspalte -> Ziele. Wer die Ausgangsspalte
  // nicht vorzieht, meldet einem abgelehnt-Defect einen Fehler dafuer, dass er dort abgelegt wird,
  // wo er ohnehin liegt (aus abgelehnt fuehrt nur in_analyse, die Spalte hat also null Ziele).
  const handleDrop = (col: DefectStatus, laneKey: string) => {
    const d = dragging;
    setDragging(null);
    if (!d) return;
    if (d.laneKey !== laneKey) {
      toast.error(t('defect.dnd.otherLane', { id: d.human_id }));
      return;
    }
    if (columnFor(d.status) === col) return;
    const targets = targetsFor(d.status, col);
    const vars = {
      defectId: d.defect_id,
      humanId: d.human_id,
      testCaseHumanId: d.test_case_human_id,
    };
    if (targets.length === 1) {
      transition.mutate({ ...vars, to: targets[0] as DefectStatus });
      return;
    }
    if (targets.length > 1) {
      setDropChoice({ vars, targets, laneKey, col });
      return;
    }
    toast.error(
      t('defect.dnd.notAllowed', {
        id: d.human_id,
        from: t(`defect.status.${d.status}`),
        to: t(`defect.status.${col}`),
        allowed: DEFECT_STATUS_TRANSITIONS[d.status].map((s) => t(`defect.status.${s}`)).join(', '),
      }),
    );
  };

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('plans.noProject')}</div>;

  const sevById = new Map<string, SeverityLevel>(
    (config?.severity_levels ?? []).map((s) => [s.severity_id, s]),
  );
  const sevOf = (d: DefectListEntry) =>
    d.severity_id ? (sevById.get(d.severity_id) ?? null) : null;

  // Gefiltert wird vor dem Lane-Aufbau: so verschwinden Lanes ohne Treffer ganz, statt als leere
  // Spaltenreihen stehen zu bleiben.
  const q = textFilter.trim().toLowerCase();
  let visible = (defects ?? []).filter(
    (d) =>
      q === '' ||
      d.human_id.toLowerCase().includes(q) ||
      d.title.toLowerCase().includes(q) ||
      (d.tcode ?? '').toLowerCase().includes(q),
  );
  // #90: clientseitig wie der Freitext-Filter oben (gleicher Grund — das Board hat alle Defects
  // des Projekts ohnehin schon geladen).
  if (triageFilter === 'open') {
    visible = visible.filter((d) => !d.severity_triaged);
  }

  // #91: fuer die namentliche Reason-Benennung im Teilerfolgs-Toast (aus der VOLLEN Liste, nicht
  // nur `visible` — ein uebersprungener Defect kann inzwischen aus dem Textfilter gefallen sein)
  // und die Kopf-Checkbox, die nur die aktuell SICHTBARE (gefilterte) Menge markieren darf.
  const defectById = new Map((defects ?? []).map((d) => [d.defect_id, d.human_id]));
  const visibleIds = visible.map((d) => d.defect_id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selection.isSelected(id));
  const someVisibleSelected = visibleIds.some((id) => selection.isSelected(id));

  // Teilerfolg (Server-Vertrag Task 11): `updated + skipped.length` === deduplizierte Eingabe.
  // Uebersprungene werden NAMENTLICH benannt (human_id + Grund), nicht nur gezaehlt — sonst weiss
  // der Nutzer nicht, WELCHER der markierten Defects die Aktion verfehlt hat.
  function announceBulkResult(result: {
    updated: number;
    skipped: { defect_id: string; reason: string }[];
  }) {
    if (result.skipped.length === 0) {
      toast.success(t('defect.bulk.updated', { count: result.updated }));
      return;
    }
    const details = result.skipped
      .map(
        (s) =>
          `${defectById.get(s.defect_id) ?? s.defect_id} (${t(`defect.bulk.reason.${s.reason}`)})`,
      )
      .join(', ');
    toast.info(
      t('defect.bulk.partial', {
        done: result.updated,
        total: result.updated + result.skipped.length,
        details,
      }),
    );
  }

  // Swim-Lanes je Teilprojekt/Stream (DEF-4).
  const laneMap = new Map<string, Lane>();
  for (const d of visible) {
    const key = d.node_id ?? '__none__';
    const lane = laneMap.get(key) ?? { node_id: d.node_id, node_name: d.node_name, items: [] };
    lane.items.push(d);
    laneMap.set(key, lane);
  }
  const lanes = [...laneMap.values()];

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-text-primary">{t('defect.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* #39: manueller Anlegeweg — ohne ihn entsteht ein Defect nur aus einem Fehler-Lauf. */}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-btn bg-brand-accent px-3 py-1 text-sm text-white"
          >
            {t('defect.create.action')}
          </button>
          <div className="flex overflow-hidden rounded-chip border border-[var(--border)] text-xs">
            {(['kanban', 'table'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-2 py-1 ${view === v ? 'bg-brand-accent text-white' : 'text-text-muted'}`}
              >
                {t(`defect.view.${v}`)}
              </button>
            ))}
          </div>
          <input
            type="search"
            aria-label={t('defect.filter.text')}
            placeholder={t('defect.filter.text')}
            value={textFilter}
            onChange={(e) => setTextFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
          <select
            aria-label={t('defect.filter.severity')}
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          >
            <option value="">
              {t('defect.filter.severity')}: {t('defect.filter.all')}
            </option>
            {(config?.severity_levels ?? []).map((s) => (
              <option key={s.severity_id} value={s.severity_id}>
                {pickLang(lang, s.label_de, s.label_en)}
              </option>
            ))}
          </select>
          <SearchSelect
            label={t('defect.filter.assignee')}
            value={assigneeFilter}
            onChange={setAssigneeFilter}
            options={(members ?? []).map((m) => ({ value: m.user_id, label: m.name }))}
            emptyLabel={`${t('defect.filter.assignee')}: ${t('defect.filter.all')}`}
            className="w-48 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
          <select
            aria-label={t('defect.filter.plan')}
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          >
            <option value="">
              {t('defect.filter.plan')}: {t('defect.filter.all')}
            </option>
            {(plans ?? []).map((p) => (
              <option key={p.test_plan_id} value={p.test_plan_id}>
                {p.name}
              </option>
            ))}
            {/* Dritter Zustand: der manuell angelegte Defect ohne Planbezug bleibt auffindbar. */}
            <option value="none">{t('defect.noPlan')}</option>
          </select>
          <select
            aria-label={t('defect.filter.triageLabel')}
            value={triageFilter}
            onChange={(e) => setTriageFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          >
            <option value="">{t('defect.filter.triageAll')}</option>
            <option value="open">{t('defect.filter.triageOpen')}</option>
          </select>
        </div>
      </div>

      {/* #108: eine Zeile fuer alle drei Sekundaer-Queries (config/members/plans) — Retry trifft
          gezielt nur die Gescheiterten (#89-Muster), statt alle drei blind neu zu laden. */}
      {configQuery.isError || membersQuery.isError || plansQuery.isError ? (
        <QueryErrorNotice
          compact
          onRetry={() => {
            if (configQuery.isError) void configQuery.refetch();
            if (membersQuery.isError) void membersQuery.refetch();
            if (plansQuery.isError) void plansQuery.refetch();
          }}
        />
      ) : null}

      {isLoading && <div className="text-sm text-text-muted">{t('common.loading')}</div>}
      {/* #89: Karte statt Leerzustand — sonst feiert `visible.length === 0` einen gescheiterten
          Fetch als "keine Defects" (Kernsatz: ein Query-Fehler darf NIE als Leerzustand erscheinen). */}
      {isError && <QueryErrorNotice onRetry={() => defectsQuery.refetch()} />}
      {!isLoading && !isError && visible.length === 0 && (
        <div className="text-sm text-text-muted">{t('defect.empty')}</div>
      )}

      {view === 'table' && visible.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase text-text-muted">
            <tr>
              {/* #91: Kopf-Checkbox markiert/entmarkiert nur die SICHTBARE (gefilterte) Menge — nur
                  bei defect.edit, dieselbe Gatung wie die Checkbox-Spalte selbst. */}
              {canBulk && (
                <th className="p-2">
                  <input
                    type="checkbox"
                    aria-label={t('defect.bulk.selectAllVisible')}
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }}
                    onChange={() => selection.setMany(visibleIds, !allVisibleSelected)}
                  />
                </th>
              )}
              <th className="p-2">ID</th>
              <th className="p-2">{t('defect.filter.severity')}</th>
              <th className="p-2">{t('defect.titleField')}</th>
              <th className="p-2">Status</th>
              <th className="p-2">{t('defect.plan')}</th>
              <th className="p-2">{t('defect.filter.assignee')}</th>
              <th className="p-2">Tcode</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => (
              <tr
                key={d.defect_id}
                tabIndex={0}
                onClick={() => openFrom(d)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openFrom(d);
                }}
                className="cursor-pointer border-t border-[var(--border)] hover:bg-bg-subtle"
              >
                {/* #91: eigene Checkbox-Spalte fuer die Mehrfachauswahl — onClick stoppt die
                    Weitergabe an das Zeilen-onClick (openFrom), sonst oeffnet jeder Markier-Klick
                    zugleich das Detail-Panel. Das Zeilen-onKeyDown bleibt bewusst unangetastet. */}
                {canBulk && (
                  <td className="p-2">
                    <input
                      type="checkbox"
                      aria-label={t('defect.bulk.selectRow', { title: d.title })}
                      checked={selection.isSelected(d.defect_id)}
                      onChange={() => selection.toggle(d.defect_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                )}
                <td className="p-2">
                  <IdLabel id={d.human_id} />
                </td>
                <td className="p-2">
                  <SeverityBadge severity={sevOf(d)} />
                </td>
                <td className="p-2 text-text-primary">
                  {d.title}
                  {/* #188: Testfall-Titel als eigene Zeile unter dem Defect-Titel. */}
                  <TestCaseTitleLine
                    titleDe={d.test_case_title_de}
                    titleEn={d.test_case_title_en}
                  />
                </td>
                <td className="p-2 text-text-body">{t(`defect.status.${d.status}`)}</td>
                {/* #57: hier liest jemand mehrere Defects nebeneinander — der Platz, an dem die
                    Plan-Angabe traegt (auf der Karte traegt sie nicht, siehe Spec §5.3). */}
                <td className="p-2 text-text-muted">{d.test_plan_name ?? t('defect.noPlan')}</td>
                <td className="p-2 text-text-body">{d.assignee_name ?? t('defect.unassigned')}</td>
                <td className="p-2 text-text-muted">{d.tcode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* #91: Aktionsleiste NUR in der Tabellenansicht, nur bei defect.edit und nur mit Auswahl —
          Kanban bekommt bewusst nichts (kein Server-Endpunkt-Bedarf dort, siehe Brief). */}
      {view === 'table' && canBulk && selection.selected.size > 0 && (
        // Fix 4 (Spec-Abweichung, Plan-Vorgabe): sticky am unteren Tabellenrand, Klassen von der
        // Scope-Tabellen-Leiste uebernommen (bulk-action-bar.tsx) — Zielschirm 1280×800, die Leiste
        // muss ohne Scrollen erreichbar sein, waehrend man oben markiert.
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-[var(--border)] bg-bg-surface px-4 py-2 text-xs shadow-md">
          <span className="font-semibold text-text-primary">
            {t('defect.bulk.selected', { count: selection.selected.size })}
          </span>
          <select
            aria-label={t('defect.bulk.setSeverity')}
            value={bulkSeverityId}
            disabled={bulkUpdate.isPending}
            onChange={(e) => setBulkSeverityId(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-primary"
          >
            <option value="">{t('defect.bulk.setSeverity')}</option>
            {(config?.severity_levels ?? []).map((s) => (
              <option key={s.severity_id} value={s.severity_id}>
                {pickLang(lang, s.label_de, s.label_en)}
              </option>
            ))}
          </select>
          {/* Review-Fix Task 14 (Important 2, Kontrolleur-Entscheid): symmetrisch zur Severity —
              die Auswahl allein loest nichts aus, erst der gemeinsame Uebernehmen-Knopf unten.
              „Bearbeiter entfernen" ist eine Select-Option (Sonderwert REMOVE_ASSIGNEE) statt ein
              eigener Knopf. */}
          <SearchSelect
            label={t('defect.bulk.assign')}
            value={bulkAssigneeId}
            disabled={bulkUpdate.isPending}
            onChange={setBulkAssigneeId}
            options={[
              ...(members ?? []).map((m) => ({ value: m.user_id, label: m.name })),
              { value: REMOVE_ASSIGNEE, label: t('defect.bulk.removeAssignee') },
            ]}
            emptyLabel={t('defect.bulk.assign')}
            className="w-44 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-primary"
          />
          <button
            type="button"
            disabled={(!bulkSeverityId && !bulkAssigneeId) || bulkUpdate.isPending}
            onClick={() => {
              const patch: { severity_id?: string; assignee_id?: string | null } = {};
              if (bulkSeverityId) patch.severity_id = bulkSeverityId;
              if (bulkAssigneeId) {
                patch.assignee_id = bulkAssigneeId === REMOVE_ASSIGNEE ? null : bulkAssigneeId;
              }
              bulkUpdate.mutate({ defect_ids: [...selection.selected], patch });
            }}
            className="rounded-btn border border-[var(--border)] px-2 py-1 disabled:opacity-60"
          >
            {t('defect.bulk.apply')}
          </button>
          <button
            type="button"
            onClick={selection.clear}
            className="ml-auto text-text-muted hover:text-text-body"
          >
            {t('defect.bulk.clear')}
          </button>
        </div>
      )}

      {view === 'kanban' &&
        lanes.map((lane) => {
          const key = lane.node_id ?? '__none__';
          const collapsed = collapsedLanes.has(key);
          const blockerCount = lane.items.filter((d) => sevOf(d)?.color_token === 'blocker').length;
          // Fuer aria-controls: die Lane-ID ist bereits eindeutig (node_id oder '__none__'), nur
          // fuer eine gueltige HTML-id noch mit einem Praefix versehen.
          const laneRegionId = `defect-lane-${key}`;
          return (
            <section key={key} className="space-y-2">
              <button
                type="button"
                onClick={() => toggleLane(key)}
                aria-expanded={!collapsed}
                aria-controls={laneRegionId}
                title={t('defect.lane.toggle')}
                className="flex w-full items-center gap-1 text-left text-sm font-semibold text-text-body"
              >
                <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
                <span>{lane.node_name ?? t('defect.swimlaneNone')}</span> · {lane.items.length} ·{' '}
                {t('defect.lane.blockers', { count: blockerCount })}
              </button>
              {!collapsed && (
                <div id={laneRegionId} className="flex gap-3 overflow-x-auto">
                  {COLUMN_STATUS.map((col) => {
                    const items = lane.items.filter((d) => columnFor(d.status) === col);
                    return (
                      // Review-Fix Minor 7: position:relative genau an dieser Lane/Spalte, damit
                      // die Rueckfrage beim geteilten Endzustand (CardMenu, position:absolute) an
                      // der Ablegestelle erscheint statt am Fuss der Seite. laneKey x col ist
                      // eindeutig, das Menue steht dadurch weiterhin nur einmal im Baum.
                      <div key={col} className="relative">
                        <KanbanColumn
                          title={t(`defect.status.${col}`)}
                          laneName={lane.node_name ?? t('defect.swimlaneNone')}
                          count={items.length}
                          dropState={dropStateFor(col, key)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            handleDrop(col, key);
                          }}
                        >
                          {items.map((d) => (
                            <DefectCard
                              key={d.defect_id}
                              d={d}
                              severity={sevOf(d)}
                              onOpen={() => openFrom(d)}
                              members={members ?? []}
                              busy={pendingDefectIds.has(d.defect_id)}
                              onTransition={(to) =>
                                transition.mutate({
                                  defectId: d.defect_id,
                                  humanId: d.human_id,
                                  testCaseHumanId: d.test_case_human_id,
                                  to,
                                })
                              }
                              onAssign={(assigneeId, assigneeName) =>
                                assign.mutate({
                                  defectId: d.defect_id,
                                  humanId: d.human_id,
                                  assigneeId,
                                  assigneeName,
                                })
                              }
                              onDragStart={() =>
                                setDragging({
                                  defect_id: d.defect_id,
                                  human_id: d.human_id,
                                  test_case_human_id: d.test_case_human_id,
                                  status: d.status,
                                  laneKey: key,
                                })
                              }
                              onDragEnd={() => setDragging(null)}
                            />
                          ))}
                        </KanbanColumn>
                        {dropChoice && dropChoice.laneKey === key && dropChoice.col === col && (
                          <CardMenu
                            label={t('defect.dnd.chooseTarget')}
                            onClose={() => setDropChoice(null)}
                            items={dropChoice.targets.map((to) => ({
                              key: to,
                              label: t(`defect.status.${to}`),
                              onSelect: () => transition.mutate({ ...dropChoice.vars, to }),
                            }))}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      {openDefect && (
        <SidePanel title={`${openDefect.human_id} · ${openDefect.title}`} onClose={closePanel}>
          {/* Ohne key rekonziliert React dieselbe Komponente an derselben Stelle, wenn openDefect
              von A auf B wechselt (z. B. via Tastatur an der Backdrop vorbei) — Cluster-Hinweise,
              die Retest-Kaskaden-Banner, ein angefangener Kommentar-Entwurf und dirty uncontrolled
              Felder von A blieben dann unter B sichtbar bzw. wuerden auf B gepatcht. Der key
              erzwingt den Remount beim Defect-Wechsel. */}
          <DefectDetail key={openDefect.id} defectId={openDefect.id} />
        </SidePanel>
      )}
      {createOpen && (
        <DefectCreateDialog
          projectId={projectId}
          boardPlanFilter={planFilter}
          onClose={() => setCreateOpen(false)}
          onCreated={openFrom}
        />
      )}
      {retestGuard && (
        <RetestGuardDialog
          pending={transition.isPending}
          reason={retestGuard.reason}
          reporterName={retestGuard.reporterName}
          onCancel={() => setRetestGuard(null)}
          onConfirm={(comment) => transition.mutate({ ...retestGuard.vars, comment })}
        />
      )}
    </div>
  );
}
